// Permission flags loader with TTL caching in localStorage
// - Loads from cache if present and not expired
// - Otherwise calls backend command "GetPlatformPermissionFlagsQuery"
// - Writes fresh value to cache with TTL

import { postAuthenticatedRequest } from "../api/requests/request";
import { getSessionData } from "../interceptors/localStorageService";

export const PERMISSION_FLAGS_STORAGE_KEY = "__permission_flags__";
const LOCALSTORAGE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Track in-flight API calls to prevent concurrent requests
let isFetchingPermissions = false;
let fetchPermissionPromise = null;

function nowMs() {
  return Date.now ? Date.now() : new Date().getTime();
}

export function readPermissionFlagsFromCache() {
  try {
    const raw = window.localStorage.getItem(PERMISSION_FLAGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.expiresAt !== "number" || parsed.expiresAt <= nowMs()) {
      // expired
      clearPermissionFlagsCache();
      return null;
    }
    return Array.isArray(parsed.value) ? parsed.value : null;
  } catch (_) {
    return null;
  }
}

export function writePermissionFlagsToCache(
  value,
  ttlMs = LOCALSTORAGE_TTL_MS
) {
  console.log("writePermissionFlagsToCache", value, ttlMs);
  try {
    const payload = {
      value: Array.isArray(value) ? value : [],
      // store absolute expiry time
      expiresAt: nowMs() + Math.max(1000, Number(ttlMs || LOCALSTORAGE_TTL_MS)),
    };
    window.localStorage.setItem(
      PERMISSION_FLAGS_STORAGE_KEY,
      JSON.stringify(payload)
    );
  } catch (_) {
    // ignore storage errors
  }
}

export function clearPermissionFlagsCache() {
  try {
    window.localStorage.removeItem(PERMISSION_FLAGS_STORAGE_KEY);
  } catch (_) {
    // ignore
  }
}

async function fetchPermissionFlags() {
  console.log("fetch Permission Flags from API");
  // Prevent concurrent calls - if already fetching, return the existing promise
  if (isFetchingPermissions && fetchPermissionPromise) {
    console.log(
      "Permission flags fetch already in progress, returning existing promise"
    );
    return fetchPermissionPromise;
  }

  // Check if there's valid sessionData before making the API call
  const sessionData = getSessionData();
  if (!sessionData) {
    console.warn("Cannot fetch permissions: No valid session data");
    return null;
  }

  // Set flag and create promise
  isFetchingPermissions = true;
  fetchPermissionPromise = (async () => {
    try {
      // const response = await postAuthenticatedRequest("GetPermissionFlagsQuery", {});
      const response = await postAuthenticatedRequest(
        "GetPermissionByAuthenticationUserQuery",
        {}
      );
      const result = response?.outData?.data;
      return result;
    } finally {
      isFetchingPermissions = false;
      fetchPermissionPromise = null;
    }
  })();

  return fetchPermissionPromise;
}

export async function loadPermissionFlags({
  forceRefresh = false,
  ttlMs = LOCALSTORAGE_TTL_MS,
} = {}) {
  // Check for valid sessionData before proceeding
  const sessionData = getSessionData();
  if (!sessionData) {
    // If no valid session, return cached data if available, otherwise return empty array
    if (!forceRefresh) {
      const cached = readPermissionFlagsFromCache();
      if (cached) return cached;
    }
    return [];
  }

  if (!forceRefresh) {
    const cached = readPermissionFlagsFromCache();
    if (cached) return cached;
  }

  // Check if there's already an in-flight request before making a new one
  // This prevents duplicate calls when multiple components try to load permissions simultaneously
  if (isFetchingPermissions && fetchPermissionPromise) {
    console.log(
      "Permission flags fetch already in progress, waiting for existing request"
    );
    const fresh = await fetchPermissionPromise;
    if (fresh) {
      const enabledPermissions = Array.isArray(fresh)
        ? fresh.filter((p) => p?.enabled)
        : [];
      writePermissionFlagsToCache(enabledPermissions, ttlMs);
      return enabledPermissions;
    }
    // If the in-flight request failed, check cache again (unless forceRefresh)
    if (!forceRefresh) {
      const cachedAfterWait = readPermissionFlagsFromCache();
      if (cachedAfterWait) return cachedAfterWait;
    }
  }

  // fetchPermissionFlags now handles concurrent call prevention
  const fresh = await fetchPermissionFlags();
  // If fetch failed (e.g., no sessionData), return cached or empty array
  if (!fresh) {
    const cached = readPermissionFlagsFromCache();
    return cached || [];
  }

  //   filter out only permissions that are enabled
  const enabledPermissions = Array.isArray(fresh)
    ? fresh.filter((p) => p?.enabled)
    : [];

  writePermissionFlagsToCache(enabledPermissions, ttlMs);
  return enabledPermissions;
}
